// src/App.jsx
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import AuthWrapper from "components/protection/AuthWrapper";
import Login from "pages/Login/login";
import MainLayout from "./components/Layout/MainLayout";
import Dashboard from "./pages/dashboard";
import TransaksiSingkong from "./pages/Transaksi Singkong/TransaksiSingkong";
import AddTransaksiSingkong from "./pages/Transaksi Singkong/Add/AddTransaksiSingkong";
import DataAdmin from "./pages/Data Pengguna/Data Admin/DataAdmin";
import AddAdmin from "./pages/Data Pengguna/Data Admin/Add/AddAdmin";
import EditAdmin from "./pages/Data Pengguna/Data Admin/Edit/EditAdmin";
import DataKaryawan from "./pages/Data Pengguna/Data Karyawan/DataKaryawan";
import AddKaryawan from "./pages/Data Pengguna/Data Karyawan/Add/AddKaryawan";
import EditKaryawan from "./pages/Data Pengguna/Data Karyawan/Edit/EditKaryawan";
import DataMitra from "./pages/Data Pengguna/Data Mitra/DataMitra";
import AddMitra from "./pages/Data Pengguna/Data Mitra/Add/AddDataMitra";
import EditMitra from "./pages/Data Pengguna/Data Mitra/Edit/EditDataMitra";
import DataKomisi from "./pages/Data Master/Data Komisi/DataKomisi";
import AddKomisi from "./pages/Data Master/Data Komisi/Add/AddKomisi";
import InvoicePreview from "./components/Print/Invoice/InvoicePreview";
import NotaPreview from "./components/Print/Nota/NotaPreview";
import STInvoicePreview from "./components/Print/STInvoice/STInvoicePreview";
import JenisPengguna from "pages/Manajemen Akses/Jenis Pengguna/JenisPengguna";
import ResetPassword from "pages/Login/ResetPassword";
import VerifyCode from "pages/Login/VerifyCode";
import NewPassword from "pages/Login/NewPassword";
import UserSetup from "pages/Data Pengguna/User Setup/userSetup";
import AdminSetup from "pages/Data Pengguna/User Setup/AdminSetup";
import KaryawanSetup from "pages/Data Pengguna/User Setup/KaryawanSetup";
import MitraSetup from "pages/Data Pengguna/User Setup/MitraSetup";
import SupplierSetup from "pages/Verifikasi/Verifikasi Setup/SupplierSetup";
import SubAgentAreal from "pages/Verifikasi/Verifikasi Setup/SubAgentAreal";
import SubAgentLapak from "pages/Verifikasi/Verifikasi Setup/SubAgentLapak";
import DataAngkutan from "pages/Verifikasi/Data Angkutan/DataAngkutan";
import AddAngkutan from "pages/Verifikasi/Data Angkutan/Add/AddAngkutan";
import EditAngkutan from "pages/Verifikasi/Data Angkutan/Edit/EditAngkutan";
import AnggotaKoperasi from "pages/Anggota Koperasi/anggotaKoperasi";
import EditAnggotaKoperasi from "pages/Anggota Koperasi/Edit/EditAnggotaKoperasi";
import VarietasSetup from "pages/Verifikasi/Verifikasi Setup/JenisVarietasSingkong";
import AddAnggotaKoperasi from "pages/Anggota Koperasi/Add/AddAnggotaKoperasi";

const isTokenValid = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return false;
    const decoded = jwtDecode(token);
    return decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const ProtectedRoutes = ({ setIsLoggedIn }) => (
  <AuthWrapper>
    <MainLayout setIsLoggedIn={setIsLoggedIn} />
  </AuthWrapper>
);

const AppRoutes = ({ isLoggedIn, setIsLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const syncLoginState = () => {
      const valid = isTokenValid();
      setIsLoggedIn(valid);
    };

    syncLoginState();
    window.addEventListener("storage", syncLoginState);
    return () => window.removeEventListener("storage", syncLoginState);
  }, [setIsLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      localStorage.setItem("lastPath", location.pathname);
    }
  }, [location.pathname, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && location.pathname === "/") {
      const lastPath = localStorage.getItem("lastPath");
      navigate(lastPath || "/dashboard", { replace: true });
    }
  }, [isLoggedIn, location.pathname, navigate]);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isLoggedIn ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Login setIsLoggedIn={setIsLoggedIn} />
          )
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-code" element={<VerifyCode />} />
      <Route path="/new-password" element={<NewPassword />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={<ProtectedRoutes setIsLoggedIn={setIsLoggedIn} />}
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="transaksi-singkong" element={<TransaksiSingkong />} />
        <Route
          path="add-transaksi-singkong"
          element={<AddTransaksiSingkong />}
        />
        <Route path="data-admin" element={<DataAdmin />} />
        <Route path="add-admin" element={<AddAdmin />} />
        <Route path="edit-admin" element={<EditAdmin />} />
        <Route path="data-karyawan" element={<DataKaryawan />} />
        <Route path="add-karyawan" element={<AddKaryawan />} />
        <Route path="edit-karyawan" element={<EditKaryawan />} />
        <Route path="data-mitra" element={<DataMitra />} />
        <Route path="add-mitra" element={<AddMitra />} />
        <Route path="edit-mitra" element={<EditMitra />} />
        <Route path="komisi" element={<DataKomisi />} />
        <Route path="add-komisi" element={<AddKomisi />} />
        <Route path="jenis-pengguna" element={<JenisPengguna />} />
        <Route path="user-setup" element={<UserSetup />} />
        <Route path="user-setup/admin" element={<AdminSetup />} />
        <Route path="user-setup/karyawan" element={<KaryawanSetup />} />
        <Route path="user-setup/mitra" element={<MitraSetup />} />
        <Route path="verifikasi-setup/supplier" element={<SupplierSetup />} />
        <Route
          path="verifikasi-setup/sub-agent-areal"
          element={<SubAgentAreal />}
        />
        <Route
          path="verifikasi-setup/sub-agent-lapak"
          element={<SubAgentLapak />}
        />
        <Route path="verifikasi/data-angkutan" element={<DataAngkutan />} />
        <Route path="verifikasi/data-angkutan/add" element={<AddAngkutan />} />
        <Route
          path="verifikasi/data-angkutan/edit"
          element={<EditAngkutan />}
        />
        <Route path="anggota-koperasi" element={<AnggotaKoperasi />} />
        <Route path="anggota-koperasi/edit" element={<EditAnggotaKoperasi />} />
        <Route path="verifikasi-setup/varietas-singkong" element={<VarietasSetup />} />
        <Route path="anggota-koperasi/add" element={<AddAnggotaKoperasi />} />
      </Route>

      {/* Print-only routes */}
      <Route path="/" element={<Outlet />}>
        <Route path="preview-invoice" element={<InvoicePreview />} />
        <Route path="nota-preview" element={<NotaPreview />} />
        <Route path="st-invoice-preview" element={<STInvoicePreview />} />
      </Route>

      {/* Fallback */}
      <Route
        path="*"
        element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />}
      />
    </Routes>
  );
};

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(isTokenValid());

  return (
    <Router>
      <AppRoutes isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
    </Router>
  );
};

export default App;
